/**
 * UploadFlow - Fun pet photo transformation experience
 * Step flow: Upload Photo → Choose Style → Select Product → Done!
 */
import React, { useState, useEffect } from 'react';
import { FONT_FAMILY } from '../ShopStyles';
import PetPortraitCreator from '../../components/PetPortraitCreator';

const UploadFlow = ({ onNavigate, onApplyToProducts, preSelectedStyle, preSelectedBackground, preSelectedCostume }) => {
  const [currentStep, setCurrentStep] = useState(1); // 1=Upload/Style, 2=Product, 3=Done
  const [portraitData, setPortraitData] = useState(null);

  // Inject animations
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'upload-flow-animations';
    styleSheet.textContent = `
      @keyframes float {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
      }
      @keyframes wiggle {
        0%, 100% { transform: rotate(-3deg); }
        50% { transform: rotate(3deg); }
      }
      @keyframes sparkle {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.6; transform: scale(1.2); }
      }
    `;
    document.head.appendChild(styleSheet);
    return () => {
      const existing = document.getElementById('upload-flow-animations');
      if (existing) existing.remove();
    };
  }, []);

  // Steps for upload-first flow
  const steps = [
    { id: 1, emoji: '📸', label: 'Upload & Style', active: currentStep >= 1 },
    { id: 2, emoji: '🛍️', label: 'Pick Products', active: currentStep >= 2 },
    { id: 3, emoji: '🎉', label: 'Done!', active: currentStep >= 3 },
  ];
  
  const handlePortraitComplete = (result) => {
    setPortraitData(result);
    setCurrentStep(2);
    // Navigate to products with the generated image
    onApplyToProducts?.(result.image, result.petName);
  };

  return (
    <div style={styles.container}>
      {/* Compact Fun Header */}
      <div style={styles.header}>
        <div style={styles.titleRow}>
          <span style={styles.titleEmoji}>✨</span>
          <h1 style={styles.title}>Pet Portrait Creator</h1>
          <span style={styles.titleEmoji}>✨</span>
        </div>
        <p style={styles.subtitle}>Transform your furry friend into amazing art!</p>
      </div>

      {/* Step Progress Bar */}
      <div style={styles.stepsContainer}>
        <div style={styles.stepsLine}>
          <div style={{
            ...styles.stepsLineFill,
            width: currentStep === 1 ? '0%' : currentStep === 2 ? '50%' : '100%',
          }} />
        </div>
        <div style={styles.stepsRow}>
          {steps.map((step, idx) => (
            <div key={step.id} style={styles.stepItem}>
              <div style={{
                ...styles.stepCircle,
                background: step.active 
                  ? 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)'
                  : '#e5e7eb',
                transform: currentStep === step.id ? 'scale(1.15)' : 'scale(1)',
                boxShadow: currentStep === step.id 
                  ? '0 4px 16px rgba(147, 51, 234, 0.4)' 
                  : 'none',
              }}>
                <span style={styles.stepEmoji}>{step.emoji}</span>
              </div>
              <span style={{
                ...styles.stepLabel,
                color: step.active ? '#7c3aed' : '#9ca3af',
                fontWeight: currentStep === step.id ? '700' : '600',
              }}>{step.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Main Creator - NO overflow hidden */}
      <div style={styles.creatorSection}>
        <PetPortraitCreator 
          onComplete={handlePortraitComplete}
          isModal={false}
          preSelectedStyle={preSelectedStyle}
          preSelectedBackground={preSelectedBackground}
          preSelectedCostume={preSelectedCostume}
        />
      </div>

      {/* Back link */}
      <div style={{ textAlign: 'center' }}>
        <button onClick={() => onNavigate?.('home')} style={styles.backLink}>
          ← Back to Shop
        </button>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '16px 0 32px',
    maxWidth: '700px',
    margin: '0 auto',
  },
  
  // Compact header
  header: {
    textAlign: 'center',
    marginBottom: '20px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '4px',
  },
  title: {
    fontSize: '32px',
    fontWeight: '800',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    margin: 0,
  },
  titleEmoji: {
    fontSize: '32px',
    animation: 'sparkle 2s ease-in-out infinite',
  },
  subtitle: {
    fontSize: '16px',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
    margin: 0,
  },
  
  // Step progress
  stepsContainer: {
    position: 'relative',
    marginBottom: '24px',
    padding: '0 20px',
  },
  stepsLine: {
    position: 'absolute',
    top: '24px',
    left: '15%',
    right: '15%',
    height: '4px',
    background: '#e5e7eb',
    borderRadius: '2px',
    zIndex: 0,
  },
  stepsLineFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #9333ea 0%, #ec4899 100%)',
    borderRadius: '2px',
    transition: 'width 0.5s ease',
  },
  stepsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    position: 'relative',
    zIndex: 1,
  },
  stepItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
  },
  stepCircle: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
  },
  stepEmoji: {
    fontSize: '22px',
  },
  stepLabel: {
    fontSize: '13px',
    fontFamily: FONT_FAMILY,
    transition: 'all 0.2s ease',
  },
  
  // Creator section - NO overflow hidden!
  creatorSection: {
    background: '#fff',
    borderRadius: '24px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
    // Removed overflow: hidden - this was clipping dropdowns!
  },
  
  // Back link - More prominent
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    background: '#fff',
    border: '2px solid #9333ea',
    borderRadius: '10px',
    color: '#9333ea',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: FONT_FAMILY,
    cursor: 'pointer',
    padding: '10px 20px',
    marginTop: '16px',
    marginBottom: '100px', // Extra space for footer
    boxShadow: '0 2px 8px rgba(147, 51, 234, 0.15)',
  },
};

export default UploadFlow;
